"use client";
import { useCallback, useSyncExternalStore } from "react";

/**
 * Hook that returns true if the provided media query matches.
 *
 * Reads matchMedia during render, so a client-mounted component paints with
 * the real value on its first frame instead of flashing the default. During
 * hydration React renders with defaultValue to match the server HTML, then
 * re-renders synchronously with the real value.
 *
 * @param query CSS media query string (e.g., "(min-width: 1024px)")
 * @param defaultValue Value used for server rendering and hydration (defaults to false)
 */
export function useMediaQuery(
  query: string,
  defaultValue: boolean = false
): boolean {
  const subscribe = useCallback(
    (onChange: () => void) => {
      const media = window.matchMedia(query);
      media.addEventListener("change", onChange);
      return () => media.removeEventListener("change", onChange);
    },
    [query]
  );
  return useSyncExternalStore(
    subscribe,
    () => window.matchMedia(query).matches,
    () => defaultValue
  );
}
